class PremiumProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.classList.add('is-reveal-ready');
    this.pendingButton = null;
    this.abortController = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('pointerdown', this.onButtonPointerDown);
    this.addEventListener('click', this.onButtonClick, true);
    this.addEventListener('click', this.onCardClick);
    this.addEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    this.setupObserver();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onButtonPointerDown);
    this.removeEventListener('click', this.onButtonClick, true);
    this.removeEventListener('click', this.onCardClick);
    this.removeEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    this.observer?.disconnect();
    this.abortController?.abort();
    clearTimeout(this.buttonResetTimer);
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      this.loadRecommendations();
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.classList.add('is-revealed');
        this.loadRecommendations();
        this.observer?.disconnect();
      },
      { rootMargin: '0px 0px 420px 0px', threshold: 0.02 }
    );
    this.observer.observe(this);
  }

  async loadRecommendations() {
    if (this.dataset.recommendationsPerformed === 'true' || this.isLoading) return;
    const { productId, sectionId, intent, url } = this.dataset;
    if (!productId || !sectionId || !url) return;

    this.isLoading = true;
    this.abortController?.abort();
    this.abortController = new AbortController();
    const requestUrl = `${url}&product_id=${encodeURIComponent(productId)}&section_id=${encodeURIComponent(sectionId)}&intent=${encodeURIComponent(intent || 'related')}`;

    try {
      const response = await fetch(requestUrl, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Recommendation request failed with status ${response.status}`);
      const text = await response.text();
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const source = documentFragment.querySelector('premium-product-recommendations');
      const sourceContent = source?.querySelector('[data-ppr-content]');
      if (!source || !sourceContent) throw new Error('Recommendation section markup was not returned');

      const cardCount = sourceContent.querySelectorAll('.pcs__card[data-product-url]').length;
      if (cardCount === 0 && this.dataset.hideWhenEmpty === 'true') {
        this.hidden = true;
        return;
      }

      const currentContent = this.querySelector('[data-ppr-content]');
      currentContent?.replaceWith(sourceContent);
      this.dataset.recommendationsPerformed = 'true';
      this.classList.remove('is-revealed');
      requestAnimationFrame(() => this.classList.add('is-revealed'));
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('[premium-product-recommendations] Unable to load recommendations.', error);
      if (this.dataset.hideWhenEmpty === 'true') this.hidden = true;
      else this.showLoadError();
    } finally {
      this.isLoading = false;
    }
  }

  showLoadError() {
    const skeleton = this.querySelector('.ppr__skeleton-grid');
    if (!skeleton) return;
    skeleton.outerHTML = '<div class="pcs__empty ppr__empty">Recommended products are temporarily unavailable.</div>';
  }

  onCardClick = (event) => {
    const card = event.target.closest?.('.pcs__card[data-product-url]');
    if (!card || !this.contains(card)) return;
    if (event.target.closest?.('a, button, input, select, textarea, form, product-form-component')) return;
    window.location.assign(card.dataset.productUrl);
  };

  onButtonPointerDown = (event) => {
    const button = event.target.closest?.('.pcs__button[type="submit"]');
    if (!button || !this.contains(button)) return;
    const bounds = button.getBoundingClientRect();
    this.animateButton(button, event.clientX - bounds.left, event.clientY - bounds.top);
  };

  onButtonClick = (event) => {
    const button = event.target.closest?.('.pcs__button[type="submit"]');
    if (!button || !this.contains(button)) return;
    if (!button.classList.contains('is-clicked')) this.animateButton(button, button.offsetWidth / 2, button.offsetHeight / 2);

    this.pendingButton?.classList.remove('is-loading');
    this.pendingButton?.removeAttribute('aria-busy');
    this.pendingButton = button;
    button.classList.remove('is-success', 'is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    setTimeout(this.openCartDrawer, 60);

    clearTimeout(this.buttonResetTimer);
    this.buttonResetTimer = setTimeout(() => {
      if (!button.classList.contains('is-loading')) return;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }, 4500);
  };

  onCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;
    const sourceForm = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (sourceForm && !this.contains(sourceForm)) return;
    if (!sourceForm && !this.pendingButton) return;

    const button = sourceForm?.querySelector('.pcs__button[type="submit"]') || this.pendingButton;
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    const drawerUpdate = drawer ? this.waitForDrawerUpdate(drawer) : Promise.resolve();

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) {
          this.setButtonError(button);
          return;
        }
        this.setButtonSuccess(button);
        await drawerUpdate;
        requestAnimationFrame(this.openCartDrawer);
      })
      .catch((error) => {
        this.setButtonError(button);
        if (error?.name !== 'AbortError') console.warn('[premium-product-recommendations] Cart update failed.', error);
      });
  };

  animateButton(button, x, y) {
    button.style.setProperty('--pcs-ripple-x', `${x}px`);
    button.style.setProperty('--pcs-ripple-y', `${y}px`);
    button.classList.remove('is-clicked');
    void button.offsetWidth;
    button.classList.add('is-clicked');
    setTimeout(() => button.classList.remove('is-clicked'), 720);
  }

  setButtonSuccess(button) {
    if (!button) return;
    button.classList.remove('is-loading', 'is-error');
    button.classList.add('is-success');
    button.removeAttribute('aria-busy');
    if (this.pendingButton === button) this.pendingButton = null;
    setTimeout(() => button.classList.remove('is-success'), 1400);
  }

  setButtonError(button) {
    if (!button) return;
    button.classList.remove('is-loading', 'is-success');
    button.classList.add('is-error');
    button.removeAttribute('aria-busy');
    if (this.pendingButton === button) this.pendingButton = null;
    setTimeout(() => button.classList.remove('is-error'), 900);
  }

  openCartDrawer = () => {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) {
      document.querySelector('[data-testid="cart-drawer-trigger"]')?.click();
      return;
    }
    const open = () => {
      if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open();
    };
    if (customElements.get('theme-drawer')) open();
    else customElements.whenDefined('theme-drawer').then(open);
  };

  waitForDrawerUpdate(drawer) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timeout);
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      };
      const observer = new MutationObserver(finish);
      const timeout = setTimeout(finish, 700);
      observer.observe(drawer, { childList: true, subtree: true, characterData: true });
    });
  }
}

if (!customElements.get('premium-product-recommendations')) {
  customElements.define('premium-product-recommendations', PremiumProductRecommendations);
}
